"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hubspotAPI = exports.defaultConsentState = void 0;
const isBrowser = typeof window !== 'undefined';
/**
 * Default consent state.
 *
 * All consent categories are set to false by default, to align with GDPR.
 */
exports.defaultConsentState = {
    allowed: false,
    categories: {
        analytics: false,
        advertisement: false,
        functionality: false,
        necessary: false,
    },
};
const subscribers = new Set();
let dispatcherRegistered = false;
let lastConsent = null;
/**
 * Internal dispatcher invoked by HubSpot when consent preferences change.
 * Notifies all registered subscribers and remembers the latest consent state.
 *
 * @param consent - The latest consent payload from HubSpot.
 */
const dispatcher = (consent) => {
    lastConsent = consent;
    for (const cb of subscribers) {
        cb(consent);
    }
};
/**
 * Access or initialize the global HubSpot API command queue.
 *
 * @returns The HubSpot command queue (`window._hsp`) if in a browser context;
 *          otherwise `null`.
 */
const getQueue = () => {
    if (!isBrowser)
        return null;
    window._hsp = window._hsp || [];
    return window._hsp;
};
/**
 * Ensure the internal consent change dispatcher is registered with the
 * HubSpot command queue exactly once.
 */
function ensureDispatcherRegistered() {
    if (!isBrowser || dispatcherRegistered)
        return;
    const q = getQueue();
    if (!q)
        return;
    q.push(['addPrivacyConsentListener', dispatcher]);
    dispatcherRegistered = true;
}
/**
 * Register a callback to listen for changes in HubSpot consent preferences.
 *
 * @param callback - Function to invoke when consent preferences change.
 */
const addPrivacyConsentListener = (callback) => {
    subscribers.add(callback);
    ensureDispatcherRegistered();
    if (lastConsent !== null) {
        callback(lastConsent);
    }
};
/**
 * Unregister a previously added consent callback.
 *
 * @param callback - The callback to remove.
 */
const removePrivacyConsentListener = (callback) => {
    subscribers.delete(callback);
};
/**
 * Determine if the user has granted consent for a specific category.
 *
 * @param category - The consent category to check.
 * @returns True if consent is granted for the specific category; otherwise false.
 */
const hasConsent = (category) => {
    if (category === 'necessary')
        return true;
    if (!isBrowser || !lastConsent)
        return false;
    return !!lastConsent.allowed || !!lastConsent.categories?.[category];
};
/**
 * Get the current consent preferences.
 * Falls back to the default consent state if no consent is saved.
 *
 * @returns Current consent state, excluding `previousCategories`.
 */
const getConsentPreferences = () => {
    if (!isBrowser || !lastConsent)
        return exports.defaultConsentState;
    const { previousCategories: _, ...rest } = lastConsent;
    return rest;
};
/**
 * Exported Hubspot API methods.
 */
exports.hubspotAPI = {
    addPrivacyConsentListener,
    hasConsent,
    removePrivacyConsentListener,
    getConsentPreferences,
};
